const testPercentageFormatter = (params) => {
  return params.value + '%';
}

const testHeatmapPercentageTooltipRenderer = (params) => {
  const uptimeValue = params.datum[params.colorKey];
  return {
    heading: `${params.datum[params.yKey]} ${params.datum[params.xKey]}`,
    data: [
      {
        label: params.colorName || 'Uptime',
        value: `${uptimeValue}%`
      }
    ]
  };
}

const heatmapPlayCountTooltipRenderer = (params) => {
  const playCount = params.datum[params.colorKey];
  const screenName = params.datum.screenName || 'Unknown Screen';
  const screenLocation = params.datum.screenLocation || 'Unknown Location';
  return {
    heading: screenName + ' - ' + screenLocation,
    data: [
      {
        label: 'Play Count',
        value: playCount.toLocaleString()
      }
    ]
  };
}

const assetNameAxisFormatter = (params) => {
  return params.value.split('#ID')[0].slice(0, 20);
}

const assetDateTimeAxisFormatter = (params) => {
  const hour = params.value.split('-')[0].split(' ')[1];
  if (hour == '00:00') {
    return params.value;
  } else {
    return hour;
  }
}

const assetDurationTooltipRenderer = (params) => {
  const durationInSeconds = params.datum[params.yKey];
  const assetName = params.datum[params.xKey].split('#ID')[0]; // Remove the ID part from the name
  
  const days = Math.floor(durationInSeconds / 86400);
  const hours = Math.floor((durationInSeconds % 86400) / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = durationInSeconds % 60;
  
  let formattedDuration = '';
  
  if (days > 0) {
    formattedDuration = `${days}d ${hours}h ${minutes}m ${seconds}s`;
  } else if (hours > 0) {
    formattedDuration = `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    formattedDuration = `${minutes}m ${seconds}s`;
  } else {
    formattedDuration = `${seconds}s`;
  }
  
  return `<div class="text-xl p-4 rounded-lg flex flex-col gap-2">
            <p class="mb-3 pb-2 border-b border-fontcolor/10"><strong>${assetName}</strong></p>
            <div class="flex flex-row items-center justify-between gap-2">
              <p>Total Duration:</p>
              <p><b>${formattedDuration}</b> (${durationInSeconds}s)</p>
            </div>
          </div>`;
}

const assetPlayCountTooltipRenderer = (params) => {
  const playCount = params.datum[params.yKey];
  const assetName = params.datum[params.xKey].split('#ID')[0];
  return `<div class="text-xl p-4 rounded-lg flex flex-col gap-2">
            <p class="mb-3 pb-2 border-b border-fontcolor/10"><strong>${assetName}</strong></p>
            <div class="flex flex-row items-center justify-between gap-2">
              <p>Play Count:<p>
              <p>${playCount}</p>
            </div>
          </div>`;
}


const assetsPercentageTooltipRenderer = (params) => {
  return {
    data: [
      {
        label: params.datum.type,
        value: `${params.datum.value}%`
      }
    ]
  };
}

const scatterRenewalsTooltipRenderer = (params) => {
  const name = params.datum.account_name || params.datum.reseller_name || params.datum.profile_name || params.datum.screen_name || 'Unknown';
  const dateLabel = params.datum.date_label || '';
  
  // Format the date nicely if we have it
  let formattedDate = dateLabel;
  if (dateLabel) {
    try {
      const date = new Date(params.datum.date);
      formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      // Use the date_label as fallback
      formattedDate = dateLabel;
    }
  }
  
  return {
    heading: name,
    data: [
      {
        label: 'Date',
        value: formattedDate
      }
    ]
  };
}

const renewalsByDateTooltipRenderer = (params) => {
  const date = params.datum.date || 'Unknown Date';
  const totalCount = params.datum.count || 0;
  const items = params.datum.accounts || params.datum.resellers || params.datum.profiles || params.datum.screens || [];
  
  // Build HTML string for tooltip without colored square
  let tooltipContent = `<div class="text-xl p-4 rounded-lg flex flex-col gap-2">
    <p class="mb-1 pb-2 border-b border-fontcolor/10"><strong>${date}: ${totalCount}</strong></p>`;
  
  // Add each account/reseller/profile and its renewal count
  if (items.length > 0) {
    items.forEach((item) => {
      const name = item.name || 'Unknown';
      tooltipContent += `
    <div class="flex flex-row items-center justify-between gap-2">
      <p>${name}:</p>
      <p><b>${item.count}</b></p>
    </div>`;
    });
  }
  
  tooltipContent += `</div>`;
  
  return tooltipContent;
}

const gaugePercentageFormatter = ({ value }) => {
  return `${value.toFixed(0)}%`;
}


const getCssVar = (name) => {
  return getComputedStyle(document.body).getPropertyValue(name).trim();
}

const assetTypeColorMapper = (params) => {
  const primaryColor = getCssVar("--primary-color");
  const onlineVideoColor = `rgba(${getCssVar("--green-100")}, 1)`;
  const googleTrafficColor = `rgba(${getCssVar("--green-500")}, 1)`;
  const weatherWidgetColor = `rgba(${getCssVar("--yellow-200")}, 1)`;
  const webColor = `rgba(${getCssVar("--gray-300")}, 1)`;

  const colorMap = {
    "Ads::Video": '#047090',
    "Ads::Image": '#82c6da',
    "DynamicPricing::MenuBoard": primaryColor,
    "DynamicPricing::Product": primaryColor,
    "YouTube": '#dd4b39',
    "WeatherWidget": weatherWidgetColor,
    "Web": webColor,
    "Screen": primaryColor,
    "OnlineVideo": onlineVideoColor,
    "Store::Apps::GoogleTraffic": googleTrafficColor,
    "CastDesign": '#777777'
  }

  const color = colorMap[params.datum.type];
  return { fill: color };
}

export default {
  testPercentageFormatter,
  testHeatmapPercentageTooltipRenderer,
  heatmapPlayCountTooltipRenderer,
  assetNameAxisFormatter,
  assetDateTimeAxisFormatter,
  assetDurationTooltipRenderer,
  assetPlayCountTooltipRenderer,
  assetsPercentageTooltipRenderer,
  assetTypeColorMapper,
  scatterRenewalsTooltipRenderer,
  renewalsByDateTooltipRenderer,
  gaugePercentageFormatter
};